import {
  Body,
  Callout,
  Heading,
  Icon,
  Tooltip,
} from '@assembly-js/design-system';
import { createSelector } from '@reduxjs/toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import FREE_EMAIL_DOMAIN_LIST from 'src/constants/freeEmailList';
import { AppViewMode } from 'src/constants/uiConsts';
import { PortalConfigContext } from 'src/context';
import { withGenericFormFields } from 'src/features/forms/renderer/withGenericFormFields';
import { useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store';
import {
  selectActiveCompaniesMap,
  selectShowOnboardingCallout,
} from 'src/store/clients/types';

const mutateClientValidator = z.object({
  companyId: z.string().min(1).optional(),
  cognitoEmail: z.string({ required_error: 'Enter email' }).email(),
  cognitoFirstName: z.string({ required_error: 'Enter first name' }).min(1),
  cognitoLastName: z.string({ required_error: 'Enter last name' }).min(1),
  userId: z.string().uuid().optional(),
});

export type MutateClientValidatorType = z.infer<typeof mutateClientValidator>;

export const selectEmailTldToCompanyMap = createSelector(
  [(state: RootState) => state.clients.activeClients, selectActiveCompaniesMap],
  (clients, activeCompaniesMap) =>
    clients.reduce<Map<string, string>>((acc, client) => {
      const [, emailTld] = client.fields.email.split('@');
      const isFreeEmail = FREE_EMAIL_DOMAIN_LIST.includes(emailTld);
      const { companyId } = client.fields;
      if (
        emailTld &&
        companyId &&
        activeCompaniesMap.get(companyId) && // the company exists and is not a placeholder
        !isFreeEmail && // avoid setting company for free email domains. E.g. different gmail.com users are likely not in the same company
        !acc.has(emailTld) // the email tld is not already in the map
      ) {
        acc.set(emailTld, companyId);
      }
      return acc;
    }, new Map()),
);

export function useMutateClientForm(
  isEdit: boolean,
  hasExternalAuthProvider: boolean = false,
) {
  const { t } = useTranslation();
  const validationScheme = toFormikValidationSchema(mutateClientValidator);
  const {
    features: { disableCompanies },
    viewMode,
  } = React.useContext(PortalConfigContext);
  const showOnboardingCallout = useAppSelector((state) =>
    selectShowOnboardingCallout(state, null),
  );
  const emailTldToCompanyMap = useAppSelector(selectEmailTldToCompanyMap);

  // Only disable email editing when an internal user is editing a client with external auth
  const isInternalUser = viewMode === AppViewMode.INTERNAL;
  const shouldDisableEmail =
    isEdit && isInternalUser && hasExternalAuthProvider;

  return {
    validationScheme,
    FormRenderer: withGenericFormFields<MutateClientValidatorType>({
      containerClassName: 'mb-4',
      fields: [
        {
          label: (
            <Heading className="font-semibold mt-2 mb-1" size="sm">
              First name
            </Heading>
          ),
          name: 'cognitoFirstName',
          type: 'text',
        },
        {
          label: (
            <Heading className="font-semibold mb-1" size="sm">
              Last name
            </Heading>
          ),
          name: 'cognitoLastName',
          type: 'text',
        },
        {
          label: (
            <div className="flex items-center gap-1 mb-0.5">
              <Heading className="font-semibold" size="sm">
                Email
              </Heading>
              {shouldDisableEmail && (
                <Tooltip
                  content={
                    <Body size="sm" className="max-w-[288px]">
                      This client signed up with Google. To edit their email,
                      they need to disconnect Google and create an email and
                      password.
                    </Body>
                  }
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  trigger="click"
                  triggerClassname="pointer-events-auto"
                >
                  <Icon
                    icon="Info"
                    className="h-3 w-3 text-gray-small cursor-pointer"
                  />
                </Tooltip>
              )}
            </div>
          ),
          name: 'cognitoEmail',
          type: 'email',
          disabled: shouldDisableEmail,
          className: shouldDisableEmail
            ? '[&_.MuiOutlinedInput-root:hover_fieldset]:!border-[#DFE1E4]'
            : '',
          onChange(e, { setFieldValue, values }, formikHandleChange) {
            if (
              'value' in e.target &&
              typeof e.target.value === 'string' &&
              !values.companyId
            ) {
              const [, emailTld] = e.target.value.split('@');
              if (emailTldToCompanyMap.has(emailTld)) {
                setFieldValue('companyId', emailTldToCompanyMap.get(emailTld));
              }
            }
            formikHandleChange(e);
          },
        },
        ...(!disableCompanies && !isEdit
          ? [
              {
                label: (
                  <div className="flex">
                    <Heading className="font-semibold" size="sm" tag="span">
                      {t(($) => $.company, { count: 1 })}
                    </Heading>
                    <Body className="mx-1" tag="span">
                      {' - '}
                    </Body>
                    <Body className="font-normal" tag="span">
                      {t(($) => $.forms.mutate_client.field_company_desc)}
                    </Body>
                  </div>
                ),
                name: 'companyId' as const,
                type: 'company' as const,
              },
            ]
          : []),
      ],
      prefix: (
        <>
          {showOnboardingCallout && (
            <Callout
              description={t(
                ($) => $.forms.mutate_client.onboarding_callout_description,
              )}
              variant="neutral"
            />
          )}
        </>
      ),
    }),
  };
}
